import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import TransactionCountCard from './TransactionCountCard';
import WeeklyAmountChart from './WeeklyAmountChart';
import Spinner from './Spinner';
import DiscountChart from './DiscountDashboardCard';
import AveragePPUChart from './AveragePPUChart';
import BalanceCard from './BalanceCard';
import PaymentProcessed from './PaymentProcessed';
import MobileHeader from './MobileHeader';
import MapMarker from './MapMarker';
import InfoWindow from './InfoWindow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faSignInAlt } from '@fortawesome/free-solid-svg-icons';
import { GoogleMap, LoadScriptNext } from '@react-google-maps/api';
import geocodeAddress from './geocode';


const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '5px',
  zIndex: 1,
};

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
};

const center = {
  lat: 37.0902,
  lng: -95.7129,
};

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const apiUrlStripe = process.env.REACT_APP_API_URL_STRIPE;
const apiUrl = process.env.REACT_APP_API_URL;

function Dashboard() {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [dashboardData, setDashboardData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [showPaymentIntentModal, setShowPaymentIntentModal] = useState(false);
  const [paymentIntentStatus, setPaymentIntentStatus] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [map, setMap] = useState(null);
  const [locationCoordinates, setLocationCoordinates] = useState([]); // State to store coordinates
  const subDomain = window.location.hostname.split('.')[0];

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentIntentId = urlParams.get('payment_intent');
    const preLoginPath = sessionStorage.getItem('preLoginPath');
    if (preLoginPath) {
      // console.log("Running preLoginPath in UseEffect");
      navigate(preLoginPath);
      sessionStorage.removeItem('preLoginPath');
    }

    if (paymentIntentId) {
      setIsPaymentLoading(true);
      fetch(`${apiUrlStripe}/getPaymentIntentStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentIntentId: paymentIntentId })
      })
        .then(response => response.json())
        .then(data => {
          setPaymentIntentStatus(data.status);
          setPaymentAmount((data.amount / 100).toFixed(2));
          setShowPaymentIntentModal(true);
          setIsPaymentLoading(false);
        })
        .catch(error => {
          // console.error("Error fetching payment intent status:", error);
          setIsPaymentLoading(false);
        });
    }

    if (currentUser) {
      setIsLoading(true);
      const fetchDashboardData = async () => {
        try {
          const response = await axios.post(`${apiUrl}/dashboardData?`, {
            userId: currentUser.userId,
          });
          const data = response.data;
          setDashboardData(data);
          //console.log('Response Data: ', data);

          // Fetch coordinates for each location
          const locationPromises = data["Location Data"].map(async location => {
            if (location.lat && location.lng) {
              return {
                ...location,
                lat: Number(location.lat),
                lng: Number(location.lng),
              };
            } else {
              const address = `${location.name}, ${location.city}, ${location.state}`;
              const coords = await geocodeAddress(address, googleMapsApiKey);
              return { ...location, ...coords };
            }
          });

          const locationsWithCoordinates = await Promise.all(locationPromises);
          setLocationCoordinates(locationsWithCoordinates);
          //console.log("All Locations with Coordinates:", locationsWithCoordinates); // Log all locations with coordinates

          setIsLoading(false);
        } catch (error) {
          // console.error('Error fetching dashboard data:', error);
          setIsLoading(false);
        }
      };
      fetchDashboardData();
    }
  }, [currentUser]);

  const handleCloseModal = () => {
    setShowPaymentIntentModal(false);
    const cleanURL = window.location.protocol + "//" + window.location.host + window.location.pathname;
    window.history.replaceState({}, document.title, cleanURL);
  };

  const handleMarkerClick = (location) => {
    setSelectedLocation(location);
    map.panTo({ lat: location.lat, lng: location.lng });
  };

  const handleInfoWindowClose = () => {
    setSelectedLocation(null);
  };

  if (!currentUser) {
    navigate('/');
    return null;
  }

  const weeklyAmountData = dashboardData["Weekly UserAmount Sum"]
    ? Object.keys(dashboardData["Weekly UserAmount Sum"]).map((week, index) => ({
      week: `Week ${week}`,
      weeklyAmount: dashboardData["Weekly UserAmount Sum"][week],
      key: `${week}-${index}`,
    }))
    : [];


  return (
    <>
      <MobileHeader theme={theme} />
      {showPaymentIntentModal && (
        <PaymentProcessed
          status={paymentIntentStatus}
          amount={paymentAmount}
          onClose={handleCloseModal}
        />
      )}
      <div className="flex-1 p-6 overflow-x-hidden mobile-container">
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            {/* {currentUser.balance < 100 && (
               <div className={`alert ${currentUser.balance === 0 ? 'bg-red-100 border border-red-400 text-red-700' : 'bg-yellow-100 border border-yellow-400 text-yellow-700'} px-4 py-3 rounded relative mb-4`}>
               {currentUser.balance === 0 ? 
                 "You have no funds on your cards! Fund your account now." : 
                 "Your account balance is getting low, fund your account now."
               }
             </div>
             
              )} */}
              {theme.showTankBanner !== false && (
              <div className="rounded-xl p-5 mb-6 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden border-2"
                   style={{
                     background: 'linear-gradient(135deg, #ffb70315 0%, #ffa50015 25%, #ff851015 50%, #ffb70310 100%)',
                     borderColor: '#ffb70350'
                   }}>
                {/* Background accent */}
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full -translate-y-16 translate-x-16"
                     style={{ background: 'radial-gradient(circle, #ffb70320 0%, transparent 70%)' }}></div>

                <div className="flex items-center gap-4 relative">
                  {/* Prominent Tank logo */}
                  <div className="bg-white rounded-full p-3 shadow-md flex-shrink-0">
                    <img
                      src="https://cdn.prod.website-files.com/62291652a86b53583094c14a/645299ffbb7d424d80367c1c_Tank%20Nav%20Logo.svg"
                      alt="Tank Payments Logo"
                      className="w-8 h-8"
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: '#ffb70320', color: '#b8860b' }}>
                        NEW
                      </span>
                      <h3 className="font-bold text-lg" style={{ color: '#b8860b' }}>
                        Tank Payments Available
                      </h3>
                    </div>
                    <p className="text-sm mb-3" style={{ color: '#9a7b0a' }}>
                      Now you can fund your account with Tank payments — the fast and secure way to manage your fuel account.
                    </p>
                    <div className="flex gap-3">
                      {theme.showTankSignup !== false && (
                        <a
                          href="https://secure.tankpayments.com/signup?referrer_id=acct_1ROlGV2YXSKCJ8Ge"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                          style={{
                            backgroundColor: '#ffb703',
                            ':hover': { backgroundColor: '#e6a503' }
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#e6a503'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#ffb703'}
                        >
                          Sign Up
                          <FontAwesomeIcon icon={faUserPlus} className="w-4 h-4" />
                        </a>
                      )}
                      {theme.showTankLogin !== false && (
                        <a
                          href="https://app.tankpayments.com/cash"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                          style={{
                            backgroundColor: '#666',
                            ':hover': { backgroundColor: '#555' }
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#555'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#666'}
                        >
                          Log In
                          <FontAwesomeIcon icon={faSignInAlt} className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {currentUser.hasPendingTransactions ? (
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-400 rounded-xl p-5 mb-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <div className="text-blue-800">
                    <span className="font-medium">You have pending transactions. </span>
                    <Link to="/useraccounting" className="font-bold underline hover:text-blue-600 transition-colors">
                      View them here →
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {currentUser.balance < 100 && (
                  <div className={`${currentUser.balance === 0 
                    ? 'bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-400' 
                    : 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-l-4 border-yellow-400'
                  } rounded-xl p-5 mb-6 shadow-sm`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full animate-pulse ${
                        currentUser.balance === 0 ? 'bg-red-500' : 'bg-yellow-500'
                      }`}></div>
                      <p className={`font-medium ${
                        currentUser.balance === 0 ? 'text-red-800' : 'text-yellow-800'
                      }`}>
                        {currentUser.balance === 0 ?
                          "You have no funds on your cards! Fund your account now." :
                          "Your account balance is getting low, fund your account now."
                        }
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="w-full mb-4">
              <BalanceCard theme={theme} lowBalance={currentUser.balance < 100} zeroBalance={currentUser.balance === 0} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
              <div className="bg-white rounded-lg shadow-md p-4 col-span-1 w-full h-full">
                <AveragePPUChart theme={theme} data={dashboardData} />
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 col-span-1 w-full h-full">
                <DiscountChart theme={theme} data={dashboardData} />
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 col-span-1 w-full h-full">
                <TransactionCountCard data={dashboardData} theme={theme} />
              </div>
              {/* Enhanced Map Section */}
              <div className="col-span-full bg-gradient-to-br from-white via-gray-50 to-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
                {/* Stunning Header */}
                <div className="relative bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-5 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform duration-200"
                        style={{ 
                          background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary || theme.primary})` 
                        }}
                      >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">Spend By Location</h3>
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                          Interactive spending map • Last 30 days
                        </p>
                      </div>
                    </div>
                    
                    {/* Enhanced Stats Summary */}
                    {locationCoordinates.length > 0 && (
                      <div className="hidden md:flex items-center gap-6">
                        <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                          <div className="text-xl font-bold text-gray-900">{locationCoordinates.length}</div>
                          <div className="text-xs text-gray-500 font-medium">Locations</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                          <div className="text-xl font-bold" style={{ color: theme.primary }}>
                            ${locationCoordinates.reduce((sum, loc) => sum + (loc.amount || 0), 0).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500 font-medium">Total Spent</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                          <div className="text-xl font-bold text-blue-600">
                            {selectedLocation ? selectedLocation.name : 'None'}
                          </div>
                          <div className="text-xs text-gray-500 font-medium">Selected</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Enhanced Map Container */}
                <div className="relative p-6">
                  {locationCoordinates.length > 0 ? (
                    <div className="relative">
                      {/* Map Instructions */}
                      <div className="absolute top-4 left-4 z-10 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 max-w-xs">
                        <p className="text-xs text-gray-600 font-medium">
                          <span className="text-blue-600">💡</span> Click markers to view location details
                        </p>
                      </div>
                      
                      {/* Enhanced Map Container */}
                      <div 
                        className="rounded-xl overflow-hidden shadow-inner border-2 border-gray-200 hover:border-gray-300 transition-colors duration-300"
                        style={{ 
                          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                          height: '450px'
                        }}
                      >
                        <LoadScriptNext googleMapsApiKey={googleMapsApiKey}>
                          <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%', borderRadius: '12px' }}
                            options={{
                              ...mapOptions,
                              styles: [
                                {
                                  featureType: 'all',
                                  elementType: 'geometry.fill',
                                  stylers: [{ saturation: -40 }, { lightness: 10 }]
                                },
                                {
                                  featureType: 'water',
                                  elementType: 'geometry',
                                  stylers: [{ color: '#e3f2fd' }]
                                },
                                {
                                  featureType: 'landscape',
                                  elementType: 'geometry',
                                  stylers: [{ color: '#f5f5f5' }]
                                }
                              ]
                            }}
                            center={center}
                            zoom={4}
                            onLoad={(map) => setMap(map)}
                          >
                            {window.google && map && locationCoordinates.map((location, index) => {
                              if (location.lat && location.lng) {
                                return (
                                  <MapMarker
                                    key={index}
                                    google={window.google}
                                    map={map}
                                    position={{ lat: location.lat, lng: location.lng }}
                                    onClick={() => handleMarkerClick(location)}
                                    theme={theme}
                                  />
                                );
                              } else {
                                return null;
                              }
                            })}
                            {selectedLocation && (
                              <InfoWindow
                                google={window.google}
                                map={map}
                                position={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                                onCloseClick={handleInfoWindowClose}
                                content={`
                                  <div style="padding: 12px; font-family: system-ui; max-width: 250px;">
                                    <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #1f2937;">${selectedLocation.name}</h3>
                                    <p style="margin: 0 0 4px 0; font-size: 14px; color: #6b7280;">${selectedLocation.city}, ${selectedLocation.state}</p>
                                    <div style="margin-top: 8px; padding: 8px; background: #f3f4f6; border-radius: 6px; border-left: 3px solid ${theme.primary};">
                                      <p style="margin: 0; font-size: 14px; font-weight: 600; color: #1f2937;">
                                        <strong>Amount Spent:</strong> <span style="color: ${theme.primary};">$${selectedLocation.amount.toFixed(2)}</span>
                                      </p>
                                    </div>
                                  </div>
                                `}
                              />
                            )}
                          </GoogleMap>
                        </LoadScriptNext>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-96 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                      <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-600 mb-2">No Location Data</h4>
                        <p className="text-sm text-gray-500">Start making transactions to see your spending locations on the map</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default Dashboard;

